import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DollarSign, Save, X } from 'lucide-react';
import type { AppDispatch } from '@/store';
import {
  createCostEntry,
  updateCostEntry,
  selectCostsLoading,
} from '@/store/slices/costsSlice';
import { Modal } from '@/components/ui';
import { GlassButton, GlassSelect } from '@/design-system';
import { CostCategory } from '@/types';
import type { OperationalCostEntry } from '@/types';

interface CostEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  programId: string;
  entry: OperationalCostEntry | null;
}

const getCategoryLabel = (category: CostCategory): string => {
  const labels: Record<CostCategory, string> = {
    [CostCategory.LABOR]: 'Labor',
    [CostCategory.SOFTWARE_LICENSES]: 'Software Licenses',
    [CostCategory.INFRASTRUCTURE]: 'Infrastructure',
    [CostCategory.TRAINING]: 'Training',
    [CostCategory.TRAVEL]: 'Travel',
    [CostCategory.CONTRACTOR_SERVICES]: 'Contractor Services',
    [CostCategory.OTHER]: 'Other',
  };
  return labels[category] || category;
};

const CostEntryModal: React.FC<CostEntryModalProps> = ({
  isOpen,
  onClose,
  programId,
  entry,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const loading = useSelector(selectCostsLoading);
  const isEditMode = !!entry;

  // Form state
  const [formData, setFormData] = useState({
    category: CostCategory.OTHER as CostCategory,
    description: '',
    amount: '',
    fiscalYear: new Date().getFullYear(),
    quarter: 1,
    month: '' as number | '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (entry) {
      setFormData({
        category: entry.category,
        description: entry.description,
        amount: entry.amount.toString(),
        fiscalYear: entry.fiscalYear,
        quarter: entry.quarter,
        month: entry.month || '',
      });
    } else {
      // Reset to defaults for new entry
      setFormData({
        category: CostCategory.OTHER,
        description: '',
        amount: '',
        fiscalYear: new Date().getFullYear(),
        quarter: Math.ceil((new Date().getMonth() + 1) / 3),
        month: '',
      });
    }
    setErrors({});
  }, [entry, isOpen]);

  const handleInputChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Amount must be greater than 0';
    }
    if (!formData.fiscalYear || formData.fiscalYear < 2020 || formData.fiscalYear > 2030) {
      newErrors.fiscalYear = 'Invalid fiscal year';
    }
    if (!formData.quarter || formData.quarter < 1 || formData.quarter > 4) {
      newErrors.quarter = 'Quarter must be 1-4';
    }
    if (formData.month !== '' && (formData.month < 1 || formData.month > 12)) {
      newErrors.month = 'Month must be 1-12';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const data = {
      category: formData.category,
      description: formData.description.trim(),
      amount: parseFloat(formData.amount),
      fiscalYear: formData.fiscalYear,
      quarter: formData.quarter,
      month: formData.month !== '' ? Number(formData.month) : undefined,
    };

    let result;
    if (isEditMode && entry) {
      result = await dispatch(updateCostEntry({ costId: entry.id, data }));
      if (updateCostEntry.fulfilled.match(result)) {
        onClose();
      }
    } else {
      result = await dispatch(createCostEntry({ programId, data }));
      if (createCostEntry.fulfilled.match(result)) {
        onClose();
      }
    }
  };

  const currentYear = new Date().getFullYear();
  const fiscalYearOptions = Array.from({ length: 7 }, (_, i) => ({
    value: (currentYear - 2 + i).toString(),
    label: `FY${currentYear - 2 + i}`,
  }));

  const quarterOptions = [
    { value: '1', label: 'Q1 (Jan-Mar)' },
    { value: '2', label: 'Q2 (Apr-Jun)' },
    { value: '3', label: 'Q3 (Jul-Sep)' },
    { value: '4', label: 'Q4 (Oct-Dec)' },
  ];

  const monthOptions = [
    { value: '', label: 'Not specified' },
    { value: '1', label: 'January' },
    { value: '2', label: 'February' },
    { value: '3', label: 'March' },
    { value: '4', label: 'April' },
    { value: '5', label: 'May' },
    { value: '6', label: 'June' },
    { value: '7', label: 'July' },
    { value: '8', label: 'August' },
    { value: '9', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  const categoryOptions = Object.values(CostCategory).map((cat) => ({
    value: cat,
    label: getCategoryLabel(cat),
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Cost Entry' : 'Add Cost Entry'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-[var(--text)] mb-2">
            Category <span className="text-status-error">*</span>
          </label>
          <GlassSelect
            value={formData.category}
            onChange={(value) => handleInputChange('category', value)}
            options={categoryOptions}
            fullWidth
            error={errors.category}
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-[var(--text)] mb-2">
            Description <span className="text-status-error">*</span>
          </label>
          <input
            type="text"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="e.g., Jira license renewal"
            className={`w-full px-4 py-2.5 bg-white/60 dark:bg-white/10 border ${
              errors.description ? 'border-status-error' : 'border-[var(--border)]'
            } rounded-xl text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-accent-blue/50`}
          />
          {errors.description && (
            <p className="mt-1 text-sm text-status-error">{errors.description}</p>
          )}
        </div>

        {/* Amount */}
        <div>
          <label className="block text-sm font-medium text-[var(--text)] mb-2">
            Amount <span className="text-status-error">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--muted)]">$</span>
            <input
              type="number"
              value={formData.amount}
              onChange={(e) => handleInputChange('amount', e.target.value)}
              placeholder="50000"
              step="0.01"
              min="0"
              className={`w-full pl-8 pr-4 py-2.5 bg-white/60 dark:bg-white/10 border ${
                errors.amount ? 'border-status-error' : 'border-[var(--border)]'
              } rounded-xl text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-accent-blue/50`}
            />
          </div>
          {errors.amount && (
            <p className="mt-1 text-sm text-status-error">{errors.amount}</p>
          )}
        </div>

        {/* Period Row */}
        <div className="grid grid-cols-3 gap-4">
          {/* Fiscal Year */}
          <div>
            <label className="block text-sm font-medium text-[var(--text)] mb-2">
              Fiscal Year <span className="text-status-error">*</span>
            </label>
            <GlassSelect
              value={formData.fiscalYear.toString()}
              onChange={(value) => handleInputChange('fiscalYear', parseInt(value))}
              options={fiscalYearOptions}
              fullWidth
              error={errors.fiscalYear}
            />
          </div>

          {/* Quarter */}
          <div>
            <label className="block text-sm font-medium text-[var(--text)] mb-2">
              Quarter <span className="text-status-error">*</span>
            </label>
            <GlassSelect
              value={formData.quarter.toString()}
              onChange={(value) => handleInputChange('quarter', parseInt(value))}
              options={quarterOptions}
              fullWidth
              error={errors.quarter}
            />
          </div>

          {/* Month (Optional) */}
          <div>
            <label className="block text-sm font-medium text-[var(--text)] mb-2">
              Month <span className="text-[var(--muted)]">(Optional)</span>
            </label>
            <GlassSelect
              value={formData.month.toString()}
              onChange={(value) => handleInputChange('month', value ? parseInt(value) : '')}
              options={monthOptions}
              fullWidth
              error={errors.month}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-[var(--border)]">
          <GlassButton variant="ghost" onClick={onClose} type="button">
            Cancel
          </GlassButton>
          <GlassButton
            variant="primary"
            type="submit"
            isLoading={loading.saving}
            leftIcon={<Save className="w-4 h-4" />}
          >
            {isEditMode ? 'Save Changes' : 'Add Entry'}
          </GlassButton>
        </div>
      </form>
    </Modal>
  );
};

export default CostEntryModal;
